import React, { useState, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Tooltip } from '@mui/material';
import axios from '../../../../api/axios';

// GitHub-style colors: 0 submissions = light gray, 1-3 = light green, 4-5 = medium, 6+ = dark
const LEVEL_COLORS = [
  '#ebedf0', // 0
  '#9be9a8', // 1
  '#40c463', // 2-3
  '#30a14e', // 4-5
  '#216e39', // 6+
];

function getColor(count) {
  if (count === 0) return LEVEL_COLORS[0];
  if (count <= 1) return LEVEL_COLORS[1];
  if (count <= 3) return LEVEL_COLORS[2];
  if (count <= 5) return LEVEL_COLORS[3];
  return LEVEL_COLORS[4];
}


/**
 * GitHub 风格每日活跃度网格
 */
function ActivityGrid({ selectedCourse, embedded }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  const courseOfferingId = selectedCourse?.offeringId;
  const courseTemplateId = selectedCourse?.id;
  const params = useMemo(
    () => (courseOfferingId ? { courseOfferingId } : courseTemplateId ? { courseTemplateId } : {}),
    [courseOfferingId, courseTemplateId]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get('/submissions/stats/activity-grid', { params })
      .then((res) => {
        if (!cancelled) setDays(res.data?.days || []);
      })
      .catch(() => {
        if (!cancelled) setDays([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [params]);

  const gridAndLegend = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={32} />
    </Box>
  ) : (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {Array.from({ length: 7 }, (_, rowIdx) => (
          <Box key={rowIdx} sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
            {Array.from({ length: 12 }, (_, colIdx) => {
              const cell = days[colIdx * 7 + rowIdx];
              const count = cell?.count ?? 0;
              const dateStr = cell?.date ?? '';
              const date = dateStr ? new Date(dateStr + 'T12:00:00') : null;
              const dateLabel = date ? `${date.getMonth() + 1}月${date.getDate()}日` : '-';
              return (
                <Tooltip
                  key={colIdx}
                  title={`${dateLabel}${count > 0 ? ` · ${count} 次提交` : ' · 无提交'}`}
                  placement="top"
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: 1,
                      backgroundColor: getColor(count),
                      '&:hover': { outline: '1px solid', outlineColor: 'divider' },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          少
        </Typography>
        {LEVEL_COLORS.map((c, i) => (
          <Box
            key={i}
            sx={{
              width: 18,
              height: 18,
              borderRadius: 1,
              backgroundColor: c,
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          多
        </Typography>
      </Box>
    </>
  );

  const content = (
    <>
      <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
        每日活跃
      </Typography>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
        }}
      >
        {gridAndLegend}
      </Box>
    </>
  );

  if (embedded) {
    return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{content}</Box>;
  }

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'transparent' }}>
      <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>{content}</CardContent>
    </Card>
  );
}

export default ActivityGrid;
